import React from 'react';
import { observer } from 'mobx-react-lite';
import { autorun } from 'mobx';
import { useSnackbar } from 'notistack';
import SwipeableViews from 'react-swipeable-views';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden/Hidden';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import styles from './Main.module.css';
import { useRootStore } from '../../../mobx/bridge';
import * as utils from '../../../helpers/utils';
import LoadingScreen from './LoadingScreen/LoadingScreen';
import Settings from './Settings/Settings';
import LeftSideBar from './LeftSideBar/LeftSideBar';
import InnerToolbar from './InnerToolbar/InnerToolbar';
import MessageList from './MessageList/MessageList';
import DropzoneWrapper from './DropzoneWrapper/DropzoneWrapper';
import Notification from './Notification/Notification';
import MainComponent from './MainComponent/MainComponent';
import AutoReplyModalWindow from './AutoReplyComponents/AutoReplyModalWindow/AutoReplyModalWindow';
import config from '../../../config/config';

const snackBarPosition = {
  vertical: 'bottom',
  horizontal: 'right',
};

const TabsClasses = { root: `${styles.tabsRoot}`, indicator: `${styles.tabsIndicator}` };
const TabClasses = { root: `${styles.tabRoot}` };

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const Main = observer(function Main() {
  const {
    commonStore,
    messageStore,
    settingsStore,
    sendingEvents,
    channelTypeStore,
    contactTypeStore,
  } = useRootStore();
  const { t } = useTranslation();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const settings = settingsStore.getLoggedUserSettings;

  const displayedNotifications = React.useRef([]);

  React.useEffect(() => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      debounce(() => {
        new Promise(Notification.requestPermission).then(() => {});
      }, 250);
    }

    autorun(() => {
      settingsStore?.getUserPersistentNotifications?.slice(-10).forEach((notification) => {
        if (displayedNotifications.current.some((s) => s.notification._id === notification._id)) {
          return;
        }

        const key = enqueueSnackbar(JSON.stringify(notification), {
          persist: true,
        });

        displayedNotifications.current.push({ key, notification });
      });

      displayedNotifications.current.forEach((f) => {
        if (
          !settingsStore?.getUserPersistentNotifications?.some((s) => s._id === f.notification._id)
        ) {
          closeSnackbar(f.key);
        }
      });
    });

    if (!config.isHostedLocally) {
      const script = document.createElement('script');
      script.id = 'adblock-detection';
      script.type = 'text/javascript';
      script.src = `${config.server}/adsbygoogle.js`;
      script.onerror = () => {
        sendingEvents.socketEmitAdblockDetected();
      };
      document.body.appendChild(script);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const queryPhoneNumber = utils.getQueryParameter('phoneNumber');
    const queryCustomerFirstName = utils.getQueryParameter('customerFirstName');
    const queryCustomerLastName = utils.getQueryParameter('customerLastName');

    const formattedPhoneNumber = queryPhoneNumber?.replace(/\D/g, '');

    if (formattedPhoneNumber && commonStore.isDataLoaded) {
      const phoneNumberObject = {
        _id: utils.generateGuid(),
        Number: utils.formatPhoneNumber(formattedPhoneNumber),
        TwilioFormat: `+1${formattedPhoneNumber}`,
      };

      const channelData = {
        _id: utils.generateGuid(),
        ChannelType: channelTypeStore.computedGetByTypeId(utils.ChannelTypeEnum.SMS.TypeId),
        CustomerPhoneNumber: phoneNumberObject,
        Contacts: [
          {
            _id: utils.generateGuid(),
            FirstName: queryCustomerFirstName,
            LastName: queryCustomerLastName,
            PhoneNumbers: [phoneNumberObject],
            ContactType: contactTypeStore.computedGetByTypeId(
              utils.ContactTypeEnum.Customer.TypeId
            ),
          },
        ],
        SaveCustomerNames: true,
      };

      sendingEvents.socketEmitRoom(
        channelData,
        utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
      );

      const url = new URL(window.location);
      url.searchParams.delete('phoneNumber');
      url.searchParams.delete('locationId');
      url.searchParams.delete('customerFirstName');
      url.searchParams.delete('customerLastName');
      window.history.pushState({}, '', url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonStore.isDataLoaded]);

  const onContainerFocus = () => {
    commonStore.clearChannelNotifications();
  };

  function dragAndDropFilesEnter() {
    commonStore.setDragAndDropChildElementCounter(commonStore.dragAndDropChildElementCounter + 1);
  }

  function dragAndDropFilesLeave() {
    commonStore.setDragAndDropChildElementCounter(commonStore.dragAndDropChildElementCounter - 1);
  }

  function onDragDrop() {
    commonStore.setDragAndDropChildElementCounter(0);
  }

  let snackBarContent;
  if (commonStore.isDisconnected) {
    snackBarContent =
      commonStore.reconnectAttemptCount === 0 ? (
        <span>{t('Main.Disconnected')}</span>
      ) : (
        <span>{t('Main.AttemptingToReconnect')}</span>
      );
  }

  const unseenMessagesCount = messageStore.computedGetUnseenMessages(null, null, null).length;
  commonStore.setFaviconBadgeCount(unseenMessagesCount > 0 ? ' ' : 0);

  // for mobile view
  let showSeparateControl = null;
  if (commonStore.isSettingsMenuOpen) {
    showSeparateControl = <Settings />;
  } else if (commonStore.mainComponentOpen) {
    showSeparateControl = <MainComponent />;
  }

  const userCanUploadImages =
    settings?.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.SMS.TypeId;

  const isWebChatChannel =
    settings?.SelectedChannel?.ChannelType?.TypeId === utils.ChannelTypeEnum.WebChat.TypeId;

  const dismissSnackbar = () => {
    if (commonStore.snackbarMessage) {
      commonStore.setSnackbarMessage(null);
    }
  };

  const renderMessageList = () =>
    settings?.SelectedChannel ? (
      <MessageList />
    ) : (
      <Grid container className={styles.placeholderTextContainer}>
        <h1>{t('Main.WhoToSendMessageTo')}</h1>

        <h2>{t('Main.TypeNameOrNumberInSearchBar')}</h2>
      </Grid>
    );

  return (
    <React.Fragment>
      <Notification />

      <Snackbar
        anchorOrigin={snackBarPosition}
        open={Boolean(
          commonStore.snackbarMessage || (commonStore.isDataLoaded && commonStore.isDisconnected)
        )}
        autoHideDuration={commonStore.snackbarMessage ? 3000 : null}
        onClose={dismissSnackbar}
      >
        <SnackbarContent
          className={styles.snackBarBlue}
          message={commonStore.snackbarMessage || snackBarContent}
        />
      </Snackbar>

      <LoadingScreen />

      <Grid
        component="div"
        container
        justify="flex-start"
        alignItems="flex-start"
        data-testid="connectIndex"
      >
        <Hidden smDown>
          {commonStore.isSettingsMenuOpen ? (
            <Grid
              component="div"
              item
              className={[styles.mainGrid, styles.rightBorder, styles.sideBarFixedSize].join(' ')}
              xs
            >
              <Settings />
            </Grid>
          ) : null}

          <Grid
            component="div"
            item
            className={[styles.mainGrid, styles.rightBorder, styles.sideBarFixedSize].join(' ')}
            xs
            hidden={commonStore.isSettingsMenuOpen}
          >
            <LeftSideBar />
          </Grid>

          <Grid
            component="div"
            container
            item
            justify="flex-start"
            alignItems="flex-start"
            className={styles.mainGrid}
            xs
          >
            <Grid component="div" item xs={12} hidden={!commonStore.mainComponentOpen}>
              <MainComponent />
            </Grid>

            <Grid
              component="div"
              item
              xs={12}
              className={styles.innerToolbar}
              hidden={commonStore.mainComponentOpen}
            >
              <InnerToolbar />
            </Grid>

            <Grid component="div" container item xs={12}>
              <Grid
                component="div"
                item
                xs
                className={styles.innerGrid}
                hidden={commonStore.mainComponentOpen}
                onClick={onContainerFocus}
                onTouchStart={onContainerFocus}
                onDragEnter={dragAndDropFilesEnter}
                onDragLeave={dragAndDropFilesLeave}
                onDrop={onDragDrop}
              >
                {userCanUploadImages && !isWebChatChannel ? (
                  <DropzoneWrapper channel={settings ? settings.SelectedChannel : null} />
                ) : null}

                {renderMessageList()}
              </Grid>

              {commonStore.rightSideComponentOpen ? (
                <Grid
                  component="div"
                  item
                  xs
                  className={
                    commonStore.rightSideComponentOpen
                      ? [styles.innerGrid, styles.leftBorder, styles.sideBarFixedSize].join(' ')
                      : [styles.innerGrid, styles.leftBorder].join(' ')
                  }
                  hidden={commonStore.mainComponentOpen}
                >
                  {commonStore.rightSideComponentOpen.component}
                </Grid>
              ) : null}
            </Grid>
          </Grid>
        </Hidden>

        {/* mobile view */}

        <Hidden mdUp>
          <Tabs
            value={commonStore.selectedTab}
            onChange={commonStore.setSelectedTab}
            variant="fullWidth"
            classes={TabsClasses}
          >
            <Tab label={t('Main.ChannelsList')} classes={TabClasses} />

            <Tab label={t('Main.Messages')} classes={TabClasses} />
          </Tabs>

          {showSeparateControl ? (
            <Grid component="div" item xs={12} className={styles.mainGrid}>
              {showSeparateControl}
            </Grid>
          ) : (
            <SwipeableViews
              axis="x"
              index={commonStore.selectedTab}
              onChangeIndex={commonStore.setSelectedTabIndex}
            >
              {/* tab 1 */}

              <Grid component="div" item xs={12} className={styles.mainGrid}>
                <LeftSideBar />
              </Grid>

              {/* tab 2 */}

              <Grid
                component="div"
                container
                item
                justify="flex-start"
                alignItems="flex-start"
                className={styles.mainGrid}
                xs={12}
              >
                <Grid component="div" item xs={12} className={styles.innerToolbar}>
                  <InnerToolbar />
                </Grid>

                {commonStore.rightSideComponentOpen ? (
                  <Grid component="div" item xs={12} className={styles.innerGrid}>
                    {commonStore.rightSideComponentOpen.component}
                  </Grid>
                ) : (
                  <Grid
                    component="div"
                    item
                    xs={12}
                    className={styles.innerGrid}
                    onClick={onContainerFocus}
                    onTouchStart={onContainerFocus}
                    onDragEnter={dragAndDropFilesEnter}
                    onDragLeave={dragAndDropFilesLeave}
                    onDrop={onDragDrop}
                  >
                    {userCanUploadImages && !isWebChatChannel ? (
                      <DropzoneWrapper channel={settings ? settings.SelectedChannel : null} />
                    ) : null}

                    {renderMessageList()}
                  </Grid>
                )}
              </Grid>
            </SwipeableViews>
          )}
        </Hidden>

        <AutoReplyModalWindow />
      </Grid>
    </React.Fragment>
  );
});
Main.whyDidYouRender = true;
Main.displayName = 'Main';

export default Main;
